/**
 * Phase 810 — OperationsWidget
 *
 * Reusable expandable-accordion widget for the Operations Dashboard.
 *   • Header row is always visible and shows the top-priority row.
 *   • Clicking the header (or the caret) reveals the full sorted list.
 *   • Every row uses a coloured priority dot:
 *       red    → overdue / expired
 *       amber  → expiring / due soon
 *       green  → up to date
 *       zinc   → no data on file
 *   • Each row is clickable and navigates to the building's detail
 *     view (with an optional deep-link `tab=`).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretDown, CaretUp } from '@phosphor-icons/react';

const STATUS_META = {
  expired:  { dot: 'bg-red-500',     ring: 'ring-red-500',     text: 'text-red-700',     bg: '' },
  expiring: { dot: 'bg-amber-500',   ring: 'ring-amber-500',   text: 'text-amber-700',   bg: '' },
  ok:       { dot: 'bg-emerald-500', ring: 'ring-emerald-500', text: 'text-emerald-700', bg: '' },
  none:     { dot: 'bg-zinc-300',    ring: 'ring-zinc-300',    text: 'text-zinc-500',    bg: '' },
  unknown:  { dot: 'bg-zinc-300',    ring: 'ring-zinc-300',    text: 'text-zinc-500',    bg: '' },
};

export default function OperationsWidget({
  title,
  Icon,
  rows,
  renderRow,          // (row) => JSX for the row body (right side)
  buildingLinkTab,    // e.g. 'insuranceSection'; row click deep-links to /buildings/{id}?tab=...
  emptyLabel,
  testIdBase,
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const total = rows?.length || 0;
  const top = total > 0 ? rows[0] : null;

  const criticalCount = (rows || []).filter(r => r.status === 'expired').length;
  const warningCount  = (rows || []).filter(r => r.status === 'expiring').length;

  const goToBuilding = (row) => {
    if (!row?.building_id) return;
    const tab = buildingLinkTab ? `?tab=${buildingLinkTab}` : '';
    navigate(`/buildings/${row.building_id}${tab}`);
  };

  const renderRowInternal = (row, isHeader = false) => {
    const meta = STATUS_META[row.status] || STATUS_META.unknown;
    return (
      <button
        type="button"
        onClick={() => goToBuilding(row)}
        className={`w-full flex items-center gap-3 text-left px-3 py-2 hover:bg-zinc-50 transition-colors ${!isHeader ? 'border-t border-zinc-100' : ''} ${meta.bg}`}
        data-testid={`${testIdBase}-row-${row.building_id || 'na'}`}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} aria-hidden="true" />
        <span className="flex-1 min-w-0 text-sm font-semibold text-zinc-900 truncate">{row.building_name || '—'}</span>
        <span className="flex-shrink-0">{renderRow(row)}</span>
      </button>
    );
  };

  return (
    <div className="bg-white border border-zinc-200" data-testid={`${testIdBase}-widget`}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
        data-testid={`${testIdBase}-toggle`}
      >
        {Icon ? <Icon size={16} className="text-zinc-500 flex-shrink-0" /> : null}
        <h3 className="text-sm font-semibold text-zinc-900 flex-1 text-left" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h3>
        {criticalCount > 0 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm" data-testid={`${testIdBase}-critical-count`}>{criticalCount}</span>
        )}
        {warningCount > 0 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm" data-testid={`${testIdBase}-warning-count`}>{warningCount}</span>
        )}
        <span className="text-[10px] font-mono text-zinc-400 tabular-nums" style={{ fontVariantNumeric: 'tabular-nums' }}>{total}</span>
        {open ? <CaretUp size={12} className="text-zinc-400" /> : <CaretDown size={12} className="text-zinc-400" />}
      </button>

      {/* Body */}
      {total === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-zinc-400" data-testid={`${testIdBase}-empty`}>
          {emptyLabel || 'No data'}
        </div>
      ) : open ? (
        <div className="max-h-96 overflow-y-auto" data-testid={`${testIdBase}-list`}>
          {rows.map(r => (
            <React.Fragment key={`${r.building_id}-${r.cert_type || ''}`}>{renderRowInternal(r)}</React.Fragment>
          ))}
        </div>
      ) : (
        <div data-testid={`${testIdBase}-collapsed`}>
          {renderRowInternal(top, true)}
        </div>
      )}
    </div>
  );
}
